import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api/client';
import type { OrganisationType } from '@/lib/api/types';
import { t } from '@/lib/strings';
import { PageHeader } from '@/components/PageHeader';

/**
 * The first screen. Organisation type is set here rather than at send time, because it drives
 * the criticality wording throughout the questionnaire (F-04, specification 6).
 */
export function NewAssessmentPage() {
  const navigate = useNavigate();

  const [systemName, setSystemName] = useState('');
  const [organisationName, setOrganisationName] = useState('');
  const [organisationType, setOrganisationType] = useState<OrganisationType>('private');
  const [submitted, setSubmitted] = useState(false);

  // Detailed is the default: it is the assessment the product was built around, and the light
  // one trades resolution for speed. Chosen here because it cannot change afterwards — the
  // assessment is scored against the questions it was actually asked.
  const [scope, setScope] = useState<'light' | 'detaljeret'>('detaljeret');

  const systemNameValid = systemName.trim() !== '';

  const create = useMutation({
    mutationFn: () =>
      api.createAssessment({
        systemName: systemName.trim(),
        organisationType,
        organisationName: organisationName.trim() || null,
        scope,
      }),
    onSuccess: (assessment) => {
      navigate(`/vurdering/${assessment.id}`, {
        state: { organisationType, scope, systemName: assessment.systemName },
      });
    },
  });

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <PageHeader title={t('opsaetning.overskrift')} subtitle={t('app.beskrivelse')} />

      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 }, display: 'grid', gap: 3.5 }}>
        <TextField
          label={t('opsaetning.systemnavn')}
          helperText={
            submitted && !systemNameValid ? t('fejl.paakraevet') : t('opsaetning.systemnavnHjaelp')
          }
          value={systemName}
          onChange={(event) => setSystemName(event.target.value)}
          required
          error={submitted && !systemNameValid}
          slotProps={{ htmlInput: { maxLength: 200 } }}
        />

        <TextField
          label={t('opsaetning.organisationsnavn')}
          helperText={t('opsaetning.organisationsnavnHjaelp')}
          value={organisationName}
          onChange={(event) => setOrganisationName(event.target.value)}
          slotProps={{ htmlInput: { maxLength: 200 } }}
        />

        <Box>
          <Typography component="p" sx={{ fontWeight: 600, mb: 1 }} id="organisation-type-label">
            {t('opsaetning.organisationstype')}
          </Typography>

          <ToggleButtonGroup
            exclusive
            value={organisationType}
            onChange={(_, value: OrganisationType | null) => value && setOrganisationType(value)}
            aria-labelledby="organisation-type-label"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              '& .MuiToggleButton-root': { flex: 1, borderRadius: 1.5 },
            }}
          >
            <ToggleButton value="private">{t('opsaetning.typePrivat')}</ToggleButton>
            <ToggleButton value="public">{t('opsaetning.typeOffentlig')}</ToggleButton>
          </ToggleButtonGroup>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {t('opsaetning.typeHjaelp')}
          </Typography>
        </Box>

        <Box>
          <Typography component="p" sx={{ fontWeight: 600, mb: 1 }} id="omfang-label">
            {t('opsaetning.omfang')}
          </Typography>

          <ToggleButtonGroup
            exclusive
            value={scope}
            onChange={(_, value: 'light' | 'detaljeret' | null) => value && setScope(value)}
            aria-labelledby="omfang-label"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              '& .MuiToggleButton-root': { flex: 1, borderRadius: 1.5 },
            }}
          >
            <ToggleButton value="light">{t('opsaetning.omfangLight')}</ToggleButton>
            <ToggleButton value="detaljeret">{t('opsaetning.omfangDetaljeret')}</ToggleButton>
          </ToggleButtonGroup>

          {/* The help text follows the choice, so the trade-off is on screen at the moment it is
              made rather than buried in a tooltip. */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {scope === 'light'
              ? t('opsaetning.omfangLightHjaelp')
              : t('opsaetning.omfangDetaljeretHjaelp')}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('opsaetning.omfangHjaelp')}
          </Typography>
        </Box>

        {create.isError && (
          <Alert severity="error">
            {create.error instanceof ApiError ? create.error.userMessage : t('fejl.generel')}
          </Alert>
        )}

        <Button
          variant="contained"
          size="large"
          onClick={() => {
            setSubmitted(true);
            if (systemNameValid) {
              create.mutate();
            }
          }}
          loading={create.isPending}
          sx={{ justifySelf: 'start', px: 4 }}
        >
          {t('handling.start')}
        </Button>
      </Paper>
    </Box>
  );
}
